import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SubmissionAttachment } from '../../../../models/submissions/submission-attachment';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscribable } from '../../../../models/base/subscribable';
import { EmployerSubmissionAttachment } from '../../../../models/submissions/employer-submissions/employer-submission-attachment';
import { FileListItemComponent } from '../../../../ui/file-list-item/file-list-item.component';
import { FileDownloadService } from '../../../../services/file-download/file-download.service';
import { CommonModule } from '@angular/common';
import { FileUploadFieldComponent } from '../form-components/file-uploader/file-upload-field/file-upload-field.component';
import { FileUploaderTextComponent } from '../form-components/file-uploader/file-uploader-text/file-uploader-text.component';
import { AttachmentUtils } from '../../../../utils/attachment-utils';
import { SelfEnrolmentLogo } from '../../../../models/employers/self-enrolment-logo';
import { AssetSize } from '../../../../models/enum/dto/asset-size.enum';

@Component({
  selector: 'app-submission-file-list',
  templateUrl: './submission-file-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FileListItemComponent,
    ReactiveFormsModule,
    FileUploadFieldComponent,
    FileUploaderTextComponent
  ],
  styleUrls: [
    './submission-file-list.component.scss',
    '../../../submissions/components/ip-view-member-submission-details/internal-user-view-member-submission/internal-user-view-member-submission.component.scss'
  ]
})
export class SubmissionFileListComponent extends Subscribable {
  @Input() filesUploadedDuringSubmission: SubmissionAttachment[] | EmployerSubmissionAttachment[] = [];
  @Input() additionalFiles: SubmissionAttachment[] | EmployerSubmissionAttachment[] = [];
  @Input() canUploadFiles: boolean = false;
  @Input() submissionApproved: boolean = false;
  @Input() thumbnailFileIds: Set<string> = new Set();
  @Input() localFiles: Map<string, File> | null = null;
  @Input() logo: SelfEnrolmentLogo | null = null;
  @Output() removeFile = new EventEmitter<SubmissionAttachment>();
  @Output() fileUploaded = new EventEmitter<File>();

  private readonly fileDownloadService = inject(FileDownloadService);

  public submissionAttachmentTrackBy(index: number, item: SubmissionAttachment | EmployerSubmissionAttachment): string {
    return item.id;
  }

  public getLocalFile(file: SubmissionAttachment | EmployerSubmissionAttachment): File | null {
    return this.localFiles?.get(file.id) ?? null;
  }

  /** Local files can be previewed immediately; leave previewUrl unset so the item does not poll. */
  public getPreviewUrl(file: SubmissionAttachment | EmployerSubmissionAttachment): string | null {
    return this.getLocalFile(file) ? null : AttachmentUtils.getReadableUrl(file.attachment);
  }

  public getThumbnailUrl(file: SubmissionAttachment | EmployerSubmissionAttachment): string | null {
    return this.thumbnailFileIds.has(file.id) ? AttachmentUtils.getReadableUrl(file.attachment) : null;
  }

  public getLogoPreviewUrl(): string | null {
    return AttachmentUtils.getReadableAssetUrl(this.logo?.attachment, [
      AssetSize.Original,
      AssetSize.Large,
      AssetSize.Medium,
      AssetSize.Small,
      AssetSize.Thumb
    ]);
  }

  public getLogoThumbnailUrl(): string | null {
    return AttachmentUtils.getReadableAssetUrl(this.logo?.attachment, [
      AssetSize.Small,
      AssetSize.Thumb,
      AssetSize.Medium,
      AssetSize.Large,
      AssetSize.Original
    ]);
  }

  public onDownloadLogo(): void {
    if (this.logo?.attachment) {
      this.fileDownloadService.downloadAsset(this.logo.attachment);
    }
  }

  public onPreviewFile(file: SubmissionAttachment | EmployerSubmissionAttachment): void {
    const localFile = this.getLocalFile(file);
    if (localFile) {
      this.fileDownloadService.previewLocalFile(localFile);
    }
  }

  public onDownloadFile(file: SubmissionAttachment | EmployerSubmissionAttachment): void {
    const localFile = this.getLocalFile(file);
    if (localFile) {
      this.fileDownloadService.downloadLocalFile(localFile);
    } else {
      this.fileDownloadService.downloadAttachment(file.attachment);
    }
  }

  public formGroup: FormGroup = new FormGroup({
    file: new FormControl<File[] | null>(null)
  });

  private listenToFormGroup = this.formGroup.valueChanges.subscribeWhileAlive({
    owner: this,
    next: changes => {
      if (changes.file && changes.file.length > 0) {
        this.fileUploaded.emit(changes.file[0]);
      }
    }
  });
}
